import React, { useState, useEffect } from "react";
import {
  CssBaseline,
  ThemeProvider,
  createTheme,
  Container,
  Typography,
  Box,
  Paper,
  Link as MuiLink,
  Tooltip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import LockIcon from "@mui/icons-material/Lock";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { BrowserRouter, Routes, Route } from "react-router-dom";

import SlotMachine from "./Slots/SlotMachine";
import LoginPage from "./Pages/LoginPage";
import AdminPage from "./Pages/AdminPage";
import VF_Tool from "./Pages/VF";
import UserPage from "./Pages/UserPage";
import Unauthorized from "./Pages/Unauthorized";
import ProtectedRoute from "./Auth/ProtectedAuth";

const discordTheme = createTheme({
  palette: {
    mode: "dark",
    background: {
      default: "#0b0e14",
      paper: "#161b22",
    },
    text: {
      primary: "#f0f6fc",
      secondary: "#8b949e",
    },
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
});

// 30-Tage Uptime-Balken mit minutenbasierter Auswertung und Farbanpassung
const UptimeBars = ({ historyMap = {} }) => {
  const bars = [];
  const siteLaunchDate = new Date("2026-08-29");
  siteLaunchDate.setHours(0, 0, 0, 0);

  for (let i = 29; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    d.setHours(0, 0, 0, 0);
    
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}`;
    
    let barColor = "#30363d"; // Standard: Grau (vor Launch)
    let title = `${dateStr}: Before launch`;

    if (d >= siteLaunchDate) {
      const dayData = historyMap[dateStr];
      
      if (!dayData) {
        barColor = "#23a55a"; // Grün wenn kein Vorfall
        title = `${dateStr}: Operational (0 incidents)`;
      } else if (dayData.downPings > 0 || dayData.status === 'down') {
        const downtimeMinutes = dayData.downPings * 5; // 1 Ping = 5 Minuten
        const downtimeFormatted = downtimeMinutes >= 60 
          ? `${(downtimeMinutes / 60).toFixed(1)} hours` 
          : `${downtimeMinutes} minutes`;
        
        // Logik für die Farben (720 Minuten = 12 Stunden)
        if (downtimeMinutes < 720) {
          barColor = "#d29922"; // Gelb (unter 12h Ausfall)
        } else {
          barColor = "#f85149"; // Rot (12h oder mehr Ausfall)
        }
        
        title = `${dateStr}: Outage detected (~${downtimeFormatted} downtime)`;
      } else {
        barColor = "#23a55a"; // Grün
        title = `${dateStr}: Operational`;
      }
    }

    bars.push(
      <Tooltip key={dateStr} title={title} arrow placement="top">
        <Box
          sx={{
            flex: 1,
            height: 28,
            bgcolor: barColor,
            borderRadius: "2px",
            transition: "transform 0.1s, opacity 0.2s",
            "&:hover": { opacity: 0.7, transform: "scaleY(1.1)" },
          }}
        />
      </Tooltip>
    );
  }

  return (
    <Box sx={{ display: "flex", gap: "3px", alignItems: "center", my: 1 }}>
      {bars}
    </Box>
  );
};

// Service-Zeile mit minütlicher Uptime-Berechnung
const ServiceRow = ({ name, sub, url, data = {}, isExternal = true }) => {
  const status = data.status || "operational";
  const ms = data.ms !== undefined && data.ms > 0 ? `${data.ms}ms` : (data.ms === 0 ? "< 1ms" : "...");
  
  let statusColor = "#23a55a";
  let statusLabel = "Operational";

  if (status === "down") {
    statusColor = "#f85149";
    statusLabel = "System Outage";
  } else if (status === "degraded") {
    statusColor = "#d29922";
    statusLabel = "Degraded Performance";
  }

  const launchDate = new Date("2026-08-29");
  launchDate.setHours(0, 0, 0, 0);
  
  // Neue Uptime-Berechnung basierend auf Minuten
  let totalTrackedDays = 0;
  let totalDownPings = 0;

  for (let i = 29; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    d.setHours(0, 0, 0, 0);

    if (d >= launchDate) {
      totalTrackedDays++;
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const dateStr = `${year}-${month}-${day}`;

      const dayData = data.history && data.history[dateStr];
      if (dayData && dayData.downPings > 0) {
        totalDownPings += dayData.downPings;
      }
    }
  }

  const activeDays = Math.max(1, totalTrackedDays);
  const totalPossibleMinutes = activeDays * 24 * 60; // 1440 Minuten pro Tag
  const totalDownMinutes = totalDownPings * 5; // 5 Minuten pro Down-Ping

  // Exakte prozentuale Berechnung
  let uptimeCalc = ((totalPossibleMinutes - totalDownMinutes) / totalPossibleMinutes) * 100;
  uptimeCalc = Math.max(0, uptimeCalc); // Verhindert negative Werte
  
  // Formatierung (100% sauber anzeigen, sonst auf 2 Nachkommastellen)
  const uptimePercentage = uptimeCalc === 100 ? "100" : uptimeCalc.toFixed(2);

  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const todayHistory = data.history && data.history[todayStr];
  const todayDownPings = todayHistory ? todayHistory.downPings : 0;
  const outageMinutes = todayDownPings * 5;

  return (
    <Paper
      elevation={0}
      sx={{ 
        p: 2.5, 
        mb: 2, 
        bgcolor: "#161b22", 
        border: "1px solid #30363d", 
        borderRadius: "6px",
        transition: "border-color 0.2s",
        "&:hover": { borderColor: "#8b949e" }
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <MuiLink
            href={url}
            target={isExternal ? "_blank" : "_self"}
            rel="noreferrer"
            underline="hover"
            sx={{ color: "#f0f6fc", fontWeight: 600, fontSize: "1.05rem" }}
          >
            {name}
          </MuiLink>
          <Typography variant="body2" sx={{ color: "#8b949e", fontSize: "0.8rem" }}>
            {sub}
          </Typography>
        </Box>

        <Box sx={{ textAlign: "right" }}>
          <Typography variant="body2" sx={{ color: statusColor, fontWeight: 600, fontSize: "0.9rem" }}>
            {statusLabel}
          </Typography>
          <Typography variant="caption" sx={{ color: "#8b949e", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
            <AccessTimeIcon sx={{ fontSize: 13 }} /> {ms} • {uptimePercentage}% uptime
          </Typography>
        </Box>
      </Box>

      {/* Dokumentierter Vorfall / Ausfallzeit für heute (Farbe abhängig von < 12h oder >= 12h) */}
      {todayDownPings > 0 && (
        <Box sx={{ 
          mt: 1.5, 
          p: 1, 
          bgcolor: outageMinutes < 720 ? "rgba(210, 153, 34, 0.1)" : "rgba(248, 81, 73, 0.1)", 
          border: `1px solid ${outageMinutes < 720 ? "rgba(210, 153, 34, 0.4)" : "rgba(248, 81, 73, 0.4)"}`, 
          borderRadius: "4px" 
        }}>
          <Typography variant="caption" sx={{ color: outageMinutes < 720 ? "#d29922" : "#f85149", fontWeight: 600, display: "block" }}>
            Incident today: Service was unreachable for ~{outageMinutes >= 60 ? `${(outageMinutes/60).toFixed(1)} hours` : `${outageMinutes} minutes`} ({todayDownPings} failed checks).
          </Typography>
        </Box>
      )}

      <UptimeBars historyMap={data.history} />

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
        <Typography variant="caption" sx={{ color: "#8b949e", fontSize: "0.75rem" }}>
          Launch (Aug 29)
        </Typography>
        <Typography variant="caption" sx={{ color: "#8b949e", fontSize: "0.75rem" }}>
          {uptimePercentage}% uptime
        </Typography>
        <Typography variant="caption" sx={{ color: "#8b949e", fontSize: "0.75rem" }}>
          Today
        </Typography>
      </Box>
    </Paper>
  );
};

function StatusPage() {
  const [isLocalUser, setIsLocalUser] = useState(false);
  const [apiMetrics, setApiMetrics] = useState({});

  const BACKEND_URL = "";

  const publicServices = [
    { key: "germanmp", name: "GermanMP Network", sub: "germanmp.de • Main Infrastructure", url: "https://germanmp.de" },
    { key: "toastbot_web", name: "ToastBot Website", sub: "toastbot.app • ToastBot Bot Website", url: "https://toastbot.app" },
    { key: "toastbot_discord", name: "ToastBot Discord Application", sub: "Discord Bot Application", url: "https://discord.com" },
  ];

  const internalServices = [
    { key: "pihole", name: "Pi-hole DNS Server", sub: "Proxmox LXC Container", url: "http://192.168.2.172/admin/" },
    { key: "mariadb", name: "MariaDB / phpMyAdmin", sub: "Proxmox Container", url: "http://192.168.2.165:8080/" },
  ];

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/check-local`)
      .then((res) => res.json())
      .then((data) => setIsLocalUser(data.isLocal))
      .catch(() => setIsLocalUser(false));

    const fetchMetrics = () => {
      fetch(`${BACKEND_URL}/api/status-data`)
        .then((res) => res.json())
        .then((data) => {
          if (data && data.isLocal !== undefined) setIsLocalUser(data.isLocal);
          if (data && data.metrics) setApiMetrics(data.metrics);
        })
        .catch(() => {});
    };

    fetchMetrics();
    const interval = setInterval(fetchMetrics, 10000);
    return () => clearInterval(interval);
  }, []);

  // Dynamische Status-Logik für das Banner oben
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

  let isCurrentlyDown = false;
  let maxDowntimeToday = 0;
  let hadOutagesToday = false;

  Object.values(apiMetrics).forEach(m => {
    if (!m) return;
    if (m.status !== 'operational') {
      isCurrentlyDown = true;
    }
    
    const todayHistory = m.history && m.history[todayStr];
    const downPings = todayHistory ? todayHistory.downPings : 0;
    const outageMinutes = downPings * 5;

    if (outageMinutes > maxDowntimeToday) {
      maxDowntimeToday = outageMinutes;
    }
    if (downPings > 0) {
      hadOutagesToday = true;
    }
  });

  let bannerColor = "#23a55a"; // Standard: Grün
  let bannerText = "All Systems Operational";
  let bannerShadow = "rgba(35, 165, 90, 0.2)";
  let BannerIcon = CheckCircleIcon;

  if (isCurrentlyDown) {
    if (maxDowntimeToday < 720) {
      bannerColor = "#d29922"; // Gelb
      bannerText = "Some Systems Experiencing Issues";
      bannerShadow = "rgba(210, 153, 34, 0.2)";
      BannerIcon = WarningIcon;
    } else {
      bannerColor = "#f85149"; // Rot
      bannerText = "Some Systems Experiencing Major Outages";
      bannerShadow = "rgba(248, 81, 73, 0.2)";
      BannerIcon = WarningIcon;
    }
  } else {
    if (hadOutagesToday) {
      bannerColor = "#23a55a"; // Grün mit Hinweis
      bannerText = "All Systems Operational (Short interruptions today)";
      bannerShadow = "rgba(35, 165, 90, 0.2)";
      BannerIcon = CheckCircleIcon;
    }
  }

  return (
    <Container maxWidth="md" sx={{ pt: 6, pb: 8 }}>
      <Box sx={{ mb: 4, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, letterSpacing: "-0.5px" }}>
          GermanMP Systems Status
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 4,
          borderRadius: "6px",
          bgcolor: bannerColor,
          color: "#ffffff",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          boxShadow: `0 0 20px ${bannerShadow}`,
          transition: "background-color 0.3s, box-shadow 0.3s",
        }}
      >
        <BannerIcon sx={{ fontSize: 28 }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {bannerText}
        </Typography>
      </Paper>

      <Box sx={{ mb: 5 }}>
        <Typography variant="subtitle2" sx={{ textTransform: "uppercase", fontWeight: 700, color: "#8b949e", mb: 2, letterSpacing: "0.5px" }}>
          Public Systems
        </Typography>
        {publicServices.map((service) => (
          <ServiceRow
            key={service.key}
            name={service.name}
            sub={service.sub}
            url={service.url}
            data={apiMetrics[service.key]}
            isExternal={true}
          />
        ))}
      </Box>

      {isLocalUser && (
        <Box sx={{ mt: 5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <LockIcon sx={{ fontSize: 18, color: "#5865f2" }} />
            <Typography variant="subtitle2" sx={{ textTransform: "uppercase", fontWeight: 700, color: "#5865f2", letterSpacing: "0.5px" }}>
              Internal Infrastructure (Local Network)
            </Typography>
          </Box>
          {internalServices.map((service) => (
            <ServiceRow
              key={service.key}
              name={service.name}
              sub={service.sub}
              url={service.url}
              data={apiMetrics[service.key]}
              isExternal={false}
            />
          ))}
        </Box>
      )}

      <Box
        sx={{
          mt: 8,
          pt: 3,
          borderTop: "1px solid #30363d",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="body2" sx={{ color: "#8b949e", display: "flex", alignItems: "center", gap: 1 }}>
          Powered in Lower Lusatia
        </Typography>
      </Box>
    </Container>
  );
}

function RootPageSelector() {
  const isStatusSubdomain = window.location.hostname.startsWith("status.");
  return isStatusSubdomain ? <StatusPage /> : <SlotMachine />;
}

function App() {
  return (
    <ThemeProvider theme={discordTheme}>
      <CssBaseline />
      <BrowserRouter basename="/">
        <Routes>
          <Route path="/" element={<RootPageSelector />} />
          <Route path="/status" element={<StatusPage />} />
          <Route path="/support-tools/login" element={<LoginPage />} />
          <Route path="/dashboard/admin" element={<ProtectedRoute allowedRoles={["admin"]}><AdminPage /></ProtectedRoute>} />
          <Route path="/dashboard/vf-tool" element={<ProtectedRoute allowedRoles={["vodafone"]}><VF_Tool /></ProtectedRoute>} />
          <Route path="/dashboard/user" element={<ProtectedRoute allowedRoles={["user"]}><UserPage /></ProtectedRoute>} />
          <Route path="/unauthorized" element={<Unauthorized />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
